import yaml from 'yaml';
import { removeScreenIndexFromYaml } from './removeScreenIndex';

// this is a reduced version of the original processYaml function that lives in mod-content
// it only converts quoted strings with line breaks to block literals and removes trailing line breaks

/**
 * Project-standard `yaml` toString options.
 *
 * KEEP IN SYNC with the backend serializer
 * `mod-content/src/lib/serializeYaml.js` -> `YAML_TO_STRING` (separate repo).
 * The two must produce identical output for the same doc.
 *
 * - lineWidth 0: disable line folding entirely (this library's documented
 *   "no wrap" value - note js-yaml uses -1 for the same thing).
 * - collectionStyle 'block': force block style for all maps/sequences so the
 *   output never flips to flow style.
 */
const YAML_TO_STRING = { indent: 2, lineWidth: 0, collectionStyle: 'block' };
const CHOMPING_INDICATOR = /: \|[+-]/g;

/** Serialize a YAML document with project-standard formatting (no line wrapping). */
export function finalizeYamlDocument(doc) {
  processNode(doc.contents);

  const out = doc.toString(YAML_TO_STRING).replace(CHOMPING_INDICATOR, ': |');

  // `screenIndex` is a runtime-only property added in useContent for screen
  // numbering; it must never be persisted. Strip it at this single serialization
  // chokepoint so every save path is guaranteed clean regardless of origin -
  // notably AI patch accepts, which insert screen YAML verbatim via upsertPatch
  // and so can carry a screenIndex the AI echoed from the compiled course.
  // KEEP IN SYNC with the backend serializer mod-content/src/lib/serializeYaml.js.
  return removeScreenIndexFromYaml(out);
}

export default function processYaml(yamlContent) {
  const doc = yaml.parseDocument(yamlContent);

  // Stringify the AST back into YAML
  return { yaml: finalizeYamlDocument(doc) };
}

const getType = node => {
  let typeSymbol = Object.getOwnPropertySymbols(node).find(
    s => s.toString() === 'Symbol(yaml.node.type)'
  );

  return typeSymbol
    ? node[typeSymbol]
        .toString()
        .match(/Symbol\(yaml\.(\w+)\)/)[1]
        .toUpperCase()
    : '';
};

const processNode = node => {
  if (node == null) return;
  const type = getType(node);

  switch (type) {
    case 'SCALAR':
    case 'PLAIN': {
      const value = node.value;
      if (typeof value !== 'string') break;
      let text = value.trim();
      if (text.length > 0) {
        // if the string contains line breaks, set the node type to "BLOCK_LITERAL"
        if (
          node.type === 'QUOTE_DOUBLE' &&
          (text.includes('\n') || text.includes('\r'))
        ) {
          node.type = 'BLOCK_LITERAL';
          // remove line breaks (\n) at the end of the string
          text = text.replace(/\n$/, '');

          // replace escaped tabs with a single space
          text = text.replace(/\\t/g, ' ');
        }
        node.value = text;
      }
      break;
    }

    case 'PAIR':
      processNode(node.value);

      break;

    case 'MAP':
    case 'SEQ':
      node.items.forEach(item => {
        processNode(item);
      });

      break;
  }
};
